import { Segment, Menu, Header, Grid, Input, Dropdown, InputOnChangeData, Loader, Icon } from "semantic-ui-react";
import PermitsGrid from "./PermitsGrid";
import { AsyncQueryStatus } from "../models/AsyncActionStatus";
import { useState } from "react";
import PermitViewContainer from "../containers/PermitViewContainer";
import { useHistory, useParams } from "react-router-dom";
import { Permit_ListItem } from "../models/Permit_ListItem";
import { StateKeyList } from "../models/StateKey";
import { StateKey } from "../models/globalTypes";
import { Permit } from "../models/Permit";
import PermitsBoundariesOnMap from "./PermitsBoundariesOnMap";
import { Project } from "../models/Project";
import { PermitType } from "../models/PermitType";
import { usePermitRegisterPolicies } from "../hooks/usePermitRegisterPolicies";
import { OrgUserBase } from "../models/OrgUser";

interface AuthorizedPermitTypeItemProps {
  permitType: PermitType;
  policy: "authorizedToAddPermit" | "authorizedToRequestPermit";
  onClick: (permitTypeId: string) => void;
}

const AuthorizedPermitTypeItem = ({ permitType, policy, onClick }: AuthorizedPermitTypeItemProps) => {
  const policies = usePermitRegisterPolicies(permitType.id);
  if (!policies[policy]) return null;
  return <Dropdown.Item key={permitType.id} text={permitType.name} onClick={() => onClick(permitType.id)} />;
};

interface Props {
  showPermitBoundariesOnMap: boolean;
  showAsBuiltColumn: boolean;
  newPermitButtonVisible?: boolean;
  requestPermitButtonVisible?: boolean;
  permitTypes?: PermitType[] | null;
  permits?: Permit_ListItem[] | null;
  currentProject?: Project;
  pageCount?: number;
  stateFilter: StateKey | null;
  assignedToFilter: string;
  createdByFilter: string;
  searchString: string;
  asBuiltRequiredFilter: boolean | null;
  permitTypeFilter: string;
  onPageInfoChange: (pageSize: number, pageIndex: number) => void;
  onSearchPermits: (searchString: string) => void;
  onStateFilter: (stateKey: StateKey) => void;
  onAssignedToFilter: (assignedToId: string) => void;
  onCreatedByFilter: (createdById: string) => void;
  onAsBuiltRequiredFilterChange: (asBuiltRequired: boolean | null) => void;
  onPermitTypeFilter: (permitTypeId: string) => void;
  getPermitsStatus: AsyncQueryStatus;
  permitCreators?: OrgUserBase[];
  assignedOrgUsers?: OrgUserBase[];
  onPermitRejected?: (permit: Permit) => void;
  onPermitApproved?: (permit: Permit) => void;
  onPermitCancelled?: (permit: Permit) => void;
  onPermitUndoCancellation?: (permit: Permit) => void;
  onPermitExtended?: (permit: Permit) => void;
  onPermitCompleted?: (permit: Permit) => void;
}

const PermitsList = ({
  showPermitBoundariesOnMap,
  showAsBuiltColumn,
  newPermitButtonVisible,
  requestPermitButtonVisible,
  permitTypes,
  permits,
  currentProject,
  pageCount,
  stateFilter,
  assignedToFilter,
  createdByFilter,
  searchString,
  asBuiltRequiredFilter,
  permitTypeFilter,
  onPageInfoChange,
  onSearchPermits,
  onStateFilter,
  onAssignedToFilter,
  onCreatedByFilter,
  onAsBuiltRequiredFilterChange,
  onPermitTypeFilter,
  getPermitsStatus,
  permitCreators,
  assignedOrgUsers,
  onPermitApproved,
  onPermitCompleted,
  onPermitExtended,
  onPermitRejected,
  onPermitCancelled,
  onPermitUndoCancellation,
}: Props) => {
  const { orgName } = useParams<{ orgName: string }>();
  const { projectName } = useParams<{ projectName: string }>();
  const { registerTitle } = useParams<{ registerTitle: string }>();

  const [openViewPermitSection, setOpenViewPermitSection] = useState(false);
  const [selectedPermitToView, setSelectedPermitToView] = useState<Permit_ListItem | undefined>(undefined);
  const [listMode, setListMode] = useState("table");

  const handlePermitClicked = (permitId: string) => {
    const permitToView = permits?.find((d) => d.id === permitId);
    if (permitToView) {
      setSelectedPermitToView(permitToView);
      setOpenViewPermitSection(true);
    }
  };

  const handleViewPermitSectionClose = () => {
    setSelectedPermitToView(undefined);
    setOpenViewPermitSection(false);
  };

  const handleSearchStringChange = (e: any, { name, value }: InputOnChangeData) => {
    onSearchPermits(value);
  };

  const handleStateFilterChange = (e: any, { name, value }: any) => {
    onStateFilter(value || null);
  };

  const handleAssignedToFilterChange = (e: any, { name, value }: any) => {
    onAssignedToFilter(value || null);
  };

  const handleCreatedByFilterChange = (e: any, { name, value }: any) => {
    onCreatedByFilter(value || null);
  };

  const handleAsBuiltRequiredFilterChange = (e: any, { name, value }: any) => {
    onAsBuiltRequiredFilterChange(value === "true" ? true : value === "false" ? false : null);
  };

  const handlePermitTypeFilterChange = (e: any, { name, value }: any) => {
    onPermitTypeFilter(value || null);
  };

  let history = useHistory();
  const handleNewPermitClick = (permitTypeId: string) => {
    history.push(
      `/${orgName}/projects/${projectName}/registers/${registerTitle}/permits/add-permit?permitTypeId=${permitTypeId}`,
    );
  };

  const handleRequestPermitClick = (permitTypeId: string) => {
    history.push(
      `/${orgName}/projects/${projectName}/registers/${registerTitle}/permits/request-permit?permitTypeId=${permitTypeId}`,
    );
  };

  const stateItems = Object.values(StateKeyList).map((item) => {
    return {
      key: item.key,
      text: item.name,
      value: item.key,
    };
  });

  const permitTypeItems =
    permitTypes?.map((pt) => ({
      key: pt.id,
      text: pt.name,
      value: pt.id,
    })) ?? [];

  const assignedToItems = () => {
    return (
      assignedOrgUsers?.map((u) => ({
        key: u.id,
        text: u.user.displayName,
        value: u.id,
      })) ?? []
    );
  };

  const createdByItems = () => {
    return (
      permitCreators?.map((u) => ({
        key: u.id,
        text: u.user.displayName,
        value: u.id,
      })) ?? []
    );
  };

  return (
    <>
      <Segment basic>
        <Header as="h2">
          Permit register <Loader inline active={getPermitsStatus.loading} size="mini" />
        </Header>
      </Segment>

      <Menu tabular>
        <Menu.Item active={listMode === "table"} onClick={() => setListMode("table")}>
          <Icon name="table" />
        </Menu.Item>
        {showPermitBoundariesOnMap && (
          <Menu.Item active={listMode === "map"} onClick={() => setListMode("map")}>
            <Icon name="map" />
          </Menu.Item>
        )}

        <Menu.Menu secondary color="green" position="left">
          {newPermitButtonVisible && (
            <Dropdown
              trigger={
                <>
                  <Icon name="plus" />
                  New permit
                </>
              }
              item
            >
              <Dropdown.Menu>
                {permitTypes?.map((pt) => (
                  <AuthorizedPermitTypeItem
                    key={pt.id}
                    permitType={pt}
                    policy="authorizedToAddPermit"
                    onClick={handleNewPermitClick}
                  />
                ))}
                {!permitTypes?.length && <Dropdown.Item disabled text="No permit types configured" />}
              </Dropdown.Menu>
            </Dropdown>
          )}
          {requestPermitButtonVisible && (
            <Dropdown
              trigger={
                <>
                  <Icon name="plus" />
                  Request permit
                </>
              }
              item
            >
              <Dropdown.Menu>
                {permitTypes?.map((pt) => (
                  <AuthorizedPermitTypeItem
                    key={pt.id}
                    permitType={pt}
                    policy="authorizedToRequestPermit"
                    onClick={handleRequestPermitClick}
                  />
                ))}
                {!permitTypes?.length && <Dropdown.Item disabled text="No permit types configured" />}
              </Dropdown.Menu>
            </Dropdown>
          )}
        </Menu.Menu>
        <Menu.Menu position="right">
          {/* <Menu.Item>
            <Dropdown placeholder="Dates" basic></Dropdown>
          </Menu.Item> */}
          <Menu.Item>
            <Dropdown
              placeholder="Status"
              basic
              clearable
              selectOnBlur={false}
              selectOnNavigation={false}
              options={stateItems}
              onChange={handleStateFilterChange}
              value={stateFilter ? stateFilter.toString() : ""}
            ></Dropdown>
          </Menu.Item>
          <Menu.Item>
            <Dropdown
              placeholder="Permit Type"
              basic
              clearable
              selectOnBlur={false}
              selectOnNavigation={false}
              options={permitTypeItems}
              onChange={handlePermitTypeFilterChange}
              value={permitTypeFilter}
            ></Dropdown>
          </Menu.Item>
          <Menu.Item>
            <Dropdown
              placeholder="Assigned to"
              basic
              search
              selectOnBlur={false}
              selectOnNavigation={false}
              options={assignedToItems()}
              onChange={handleAssignedToFilterChange}
              value={assignedToFilter}
              icon={assignedToFilter ? null : "dropdown"}
            ></Dropdown>
            {assignedToFilter && (
              <Icon
                size="small"
                name="delete"
                link
                style={{ marginTop: "0.3em", marginLeft: "0.8em" }}
                onClick={() => onAssignedToFilter("" as any)}
              />
            )}
          </Menu.Item>
          <Menu.Item>
            <Dropdown
              placeholder="Created by"
              basic
              search
              selectOnBlur={false}
              selectOnNavigation={false}
              options={createdByItems()}
              onChange={handleCreatedByFilterChange}
              value={createdByFilter}
              icon={createdByFilter ? null : "dropdown"}
            ></Dropdown>
            {createdByFilter && (
              <Icon
                size="small"
                name="delete"
                link
                style={{ marginLeft: "0.8em" }}
                onClick={() => onCreatedByFilter("" as any)}
              />
            )}
          </Menu.Item>
          {showAsBuiltColumn && (
            <Menu.Item>
              <Dropdown
                placeholder="As-built"
                basic
                clearable
                selectOnBlur={false}
                selectOnNavigation={false}
                options={[
                  { key: "true", text: "As-built required", value: "true" },
                  { key: "false", text: "As-built not required", value: "false" },
                ]}
                onChange={handleAsBuiltRequiredFilterChange}
                value={asBuiltRequiredFilter ? asBuiltRequiredFilter.toString() : ""}
              ></Dropdown>
            </Menu.Item>
          )}
          <Menu.Item>
            <Input icon="search" placeholder="Search..." onChange={handleSearchStringChange} value={searchString} />
          </Menu.Item>
        </Menu.Menu>
      </Menu>

      <Segment basic style={listMode === "map" ? { paddingTop: "0" } : {}}>
        <Grid columns={openViewPermitSection ? "2" : "1"}>
          <Grid.Row>
            <Grid.Column width={openViewPermitSection ? "10" : "16"} style={listMode === "map" ? { padding: "0" } : {}}>
              {listMode === "table" && (
                <PermitsGrid
                  permits={permits}
                  selectedPermit={selectedPermitToView}
                  pageCount={pageCount}
                  onPageInfoChange={onPageInfoChange}
                  onPermitClicked={handlePermitClicked}
                  showAsBuiltColumn={showAsBuiltColumn}
                />
              )}
              {listMode === "map" && (
                <PermitsBoundariesOnMap
                  currentProject={currentProject}
                  onPermitViewClicked={handlePermitClicked}
                  permits={permits}
                />
              )}
            </Grid.Column>
            {openViewPermitSection && (
              <Grid.Column width="6">
                <PermitViewContainer
                  showPermitBoundariesOnMap={showPermitBoundariesOnMap}
                  onClose={handleViewPermitSectionClose}
                  permitId={selectedPermitToView?.id}
                  onPermitRejected={onPermitRejected}
                  onPermitApproved={onPermitApproved}
                  onPermitCancelled={onPermitCancelled}
                  onPermitUndoCancellation={onPermitUndoCancellation}
                  onPermitExtended={onPermitExtended}
                  onPermitCompleted={onPermitCompleted}
                ></PermitViewContainer>
              </Grid.Column>
            )}
          </Grid.Row>
        </Grid>
      </Segment>
    </>
  );
};

export default PermitsList;
